import { type Device, type TrackerStatus, SubType } from '@/types/devices.api'
import type { CurrentSubscription, DevicePrice, DevicePrices } from '@/types/subscriptions.api'
import { TRANSFER_SOURCE_GRACE_DAYS } from '@/shared/subscriptionTransfer'
import type { SubscriptionTransferOptions, SubscriptionTransferResult } from '@/types/subscriptionTransfer.api'

/**
 * Fake-fleet fixtures for exercising the device list / overview UI with any
 * fleet size and every subscription state, without needing real hardware on
 * the account.
 *
 * Gated by the runtime flag `window._env_.ITP_ENABLE_TEST_FIXTURES` (never set
 * in prod). When enabled, `?test-fleet=<n>` (a fleet size) or
 * `?test-fleet=<scenario>` (a named fleet, see `scenarios`) on any URL stores
 * the value in sessionStorage (sticky across navigations, like the webview
 * param) and `?test-fleet=off` clears it. The devices and subscriptions store
 * fetch actions swap their committed payload for `activeTestFleet()` — getters
 * and components see one coherent world, so grouping, badges and prev/next
 * navigation all behave as with real data.
 */

const STORAGE_KEY = 'test-fleet'

export const isTestFixturesEnabled = (): boolean => window._env_?.ITP_ENABLE_TEST_FIXTURES === 'True'

const isFleetSize = (value: string): boolean => /^\d+$/.test(value) && Number(value) > 0

/** Feed a route's `test-fleet` query value; no-ops when the flag is off. */
export const applyTestFleetQuery = (value: unknown): void => {
  if (!isTestFixturesEnabled() || typeof value !== 'string') return
  if (value === 'off') sessionStorage.removeItem(STORAGE_KEY)
  else if (isFleetSize(value) || value in scenarios) sessionStorage.setItem(STORAGE_KEY, value)
}

/** Active `test-fleet` value, or null when fixtures are off. */
const testFleetValue = (): string | null => (isTestFixturesEnabled() ? sessionStorage.getItem(STORAGE_KEY) : null)

const daysFromNow = (days: number): string => new Date(Date.now() + days * 24 * 3600 * 1000).toISOString()

// One entry per product, with the board_name / type / usage combination the
// backend really sends, so trackerProductForDevice resolves them like real
// devices (board families are matched with includes(), hence the suffixes).
type FixtureProduct = { type: Device['type']; boardName: string; usage?: string }

const products: FixtureProduct[] = [
  { type: 'tracker_06', boardName: 'LWT6M1A', usage: 'dog' }, // Biotracker
  { type: 'tracker_06', boardName: 'LWT6V2A', usage: 'cat' }, // Biotracker V2
  { type: 'tracker_01', boardName: 'LWT1B' }, // Classic
  { type: 'tracker_07', boardName: 'LWT7A' }, // Pro Max
  { type: 'tracker_03', boardName: 'LWT3C' }, // Pro
  { type: 'LWT2', boardName: 'LWT2B' }, // Mini
  { type: 'tracker_04', boardName: 'SDC1', usage: 'dog' }, // Smart Dog Collar
]

// One entry per plan state the UI distinguishes. Recurring / trial / past-due
// / cancelling are read from subscriptions/getSubscriptionBySerial, not the
// device, so those states also emit a matching current_subscription. The
// sub_type in a state is only a tier hint for biotrackers — it is remapped to
// the raw lwt6 values on tracker_06 and dropped on every other product.
type PlanState = { status: Partial<TrackerStatus>; subscription?: () => CurrentSubscription }

const recurringSubscription = (overrides: Partial<CurrentSubscription>): CurrentSubscription => ({
  amount: 995,
  currency: 'eur',
  plan_id: 'price_test_monthly',
  interval: 'month',
  interval_count: 1,
  status: 'active',
  cancel_at_period_end: false,
  current_period_end: daysFromNow(45),
  ...overrides,
})

const planStates: PlanState[] = [
  // Active recurring subscription
  {
    status: { sub_state: 'active', sub_end_date: daysFromNow(45), sub_type: SubType.plus },
    subscription: () => recurringSubscription({}),
  },
  // One-shot plan expiring within 31 days → "soon" badge
  { status: { sub_state: 'active', sub_end_date: daysFromNow(14), sub_type: SubType.max } },
  // Expired
  { status: { sub_state: 'expired', sub_end_date: daysFromNow(-60) } },
  // Never activated
  { status: { sub_state: 'not_activated' } },
  // Lite / basic plan — no paid network subscription, so the backend reports
  // not_activated alongside the free tier.
  { status: { sub_state: 'not_activated', sub_type: SubType.lite } },
  // Free trial
  {
    status: { sub_state: 'active', sub_end_date: daysFromNow(21) },
    subscription: () => recurringSubscription({ amount: 1495, plan_id: 'price_test_yearly', interval: 'year', status: 'trialing', current_period_end: daysFromNow(21), trial_end: daysFromNow(21) }),
  },
  // Recurring but payment failed
  {
    status: { sub_state: 'active', sub_end_date: daysFromNow(-3) },
    subscription: () => recurringSubscription({ status: 'past_due', current_period_end: daysFromNow(-3) }),
  },
  // Recurring, cancelled at period end
  {
    status: { sub_state: 'active', sub_end_date: daysFromNow(25) },
    subscription: () => recurringSubscription({ cancel_at_period_end: true, current_period_end: daysFromNow(25) }),
  },
]

// tracker_06 devices report the raw lwt6 tier values (free/bluetooth/connect —
// what lwt6SubType expects), not the SubType enum values, and always carry one:
// a biotracker without a paid plan is on 'free'. The cast papers over
// TrackerStatus.sub_type not modelling these raw values yet.
const lwt6RawSubType = (subType?: SubType): SubType => {
  const raw: Partial<Record<SubType, string>> = { [SubType.lite]: 'free', [SubType.plus]: 'bluetooth', [SubType.max]: 'connect' }
  return ((subType && raw[subType]) || 'free') as SubType
}

const names = [
  'Voyager 1',
  'Voyager 2',
  'Pioneer 10',
  'Pioneer 11',
  'Cassini',
  'Huygens',
  'Rosetta',
  'Philae',
  'New Horizons',
  'Juno',
  'Galileo',
  'Curiosity',
  'Perseverance',
  'Opportunity',
  'Spirit',
  'Sojourner',
  'Ingenuity',
  'Viking 1',
  'Viking 2',
  'Mariner 4',
  'Messenger',
  'Dawn',
  'InSight',
  'Kepler',
  'Gaia',
  'Hubble',
  'Webb',
  'Parker',
  'BepiColombo',
  'Hayabusa 2',
  'Sputnik',
  'Apollo 11',
]

const fixtureName = (i: number): string => {
  const round = Math.floor(i / names.length)
  return round ? `${names[i % names.length]} ${round + 1}` : names[i % names.length]
}

// Mirrors the real catalog: each non-free tier has a monthly and an annual price.
const lwt6Prices = (): DevicePrice[] => [
  {
    id: 'price_test_plus_1',
    name: 'Plus monthly',
    currency: 'eur',
    unit_amount: 495,
    tax_behavior: 'inclusive',
    meta_data: { type: 'bluetooth', months: '1', recurring: 'true', network_sub: 'true' },
  },
  {
    id: 'price_test_plus_12',
    name: 'Plus yearly',
    currency: 'eur',
    unit_amount: 4790,
    tax_behavior: 'inclusive',
    meta_data: { type: 'bluetooth', months: '12', recurring: 'true', network_sub: 'true' },
  },
  { id: 'price_test_max_1', name: 'Max monthly', currency: 'eur', unit_amount: 995, tax_behavior: 'inclusive', meta_data: { type: 'connect', months: '1', recurring: 'true', network_sub: 'true' } },
  { id: 'price_test_max_12', name: 'Max yearly', currency: 'eur', unit_amount: 9590, tax_behavior: 'inclusive', meta_data: { type: 'connect', months: '12', recurring: 'true', network_sub: 'true' } },
]

const oneShotPrices = (): DevicePrice[] => [
  { id: 'price_test_12m', name: '12 months', currency: 'eur', unit_amount: 3990, tax_behavior: 'inclusive', meta_data: { months: '12', network_sub: 'true' } },
  { id: 'price_test_36m', name: '36 months', currency: 'eur', unit_amount: 9990, tax_behavior: 'inclusive', meta_data: { months: '36', network_sub: 'true' } },
]

type TestFleet = { devices: Device[]; subscriptions: DevicePrices[] }

const fixtureSerial = (i: number): string => `TEST${String(i + 1).padStart(4, '0')}`

/** Fixture `i` of the fleet: a device of `product` in `plan`, and its per-serial subscription payload */
const fixtureDevice = (i: number, product: FixtureProduct, plan: PlanState): { device: Device; subscription: DevicePrices } => {
  const serial = fixtureSerial(i)
  return {
    device: {
      id: 900000 + i,
      name: fixtureName(i),
      type: product.type,
      serial,
      created: daysFromNow(-400 + i),
      version_build: '1.0.0-test',
      timezone: 'Europe/Paris',
      version: '1.0.0',
      tracker_config: {
        mode: '30min',
        color: i % 8,
        icon: i % 10,
        notify_position: false,
        notify_long_walk: false,
        network: 'gsm',
        network_region: 'eu',
        firmware_path: '',
        board_name: product.boardName,
        usage: product.usage,
      },
      tracker_status: {
        battery: 15 + ((i * 17) % 85),
        begin_date: daysFromNow(-400 + i),
        last_event: daysFromNow(-1),
        state: 'active',
        stationary: i % 2,
        boost_state: 0,
        ...plan.status,
        // The Basic/Plus/Max tiers only exist on biotrackers: tracker_06 always
        // carries one (raw value), every other product carries none.
        ...(product.type === 'tracker_06' ? { sub_type: lwt6RawSubType(plan.status.sub_type) } : { sub_type: undefined }),
      },
    },
    subscription: {
      serial,
      prices: product.type === 'tracker_06' ? lwt6Prices() : oneShotPrices(),
      ...(plan.subscription ? { current_subscription: plan.subscription() } : {}),
    },
  }
}

const toFleet = (fixtures: ReturnType<typeof fixtureDevice>[]): TestFleet => ({
  devices: fixtures.map((f) => f.device),
  subscriptions: fixtures.map((f) => f.subscription),
})

/**
 * Generate a coherent fleet of `n` devices plus their per-serial subscription
 * payloads by cycling product (7 entries) × plan state (8 entries) — the
 * counts are coprime, so every combination appears as n grows.
 */
export const buildTestFleet = (n: number): TestFleet => toFleet(Array.from({ length: n }, (_, i) => fixtureDevice(i, products[i % products.length], planStates[i % planStates.length])))

const proMax: FixtureProduct = { type: 'tracker_07', boardName: 'LWT7A' }
const pro: FixtureProduct = { type: 'tracker_03', boardName: 'LWT3C' }

// Upgrade: a Pro Max bought without a subscription, and the Pros it can take a subscription from
const transferTarget = () => fixtureDevice(0, proMax, { status: { sub_state: 'not_activated' } })
const yearlyPro = (i: number, days: number) => {
  const end = daysFromNow(days)
  return fixtureDevice(i, pro, {
    status: { sub_state: 'active', sub_end_date: end },
    subscription: () => recurringSubscription({ amount: 3990, plan_id: 'price_test_yearly', interval: 'year', current_period_end: end }),
  })
}
const monthlyPro = (i: number, days: number) => {
  const end = daysFromNow(days)
  return fixtureDevice(i, pro, { status: { sub_state: 'active', sub_end_date: end }, subscription: () => recurringSubscription({ current_period_end: end }) })
}
const oneShotPro = (i: number, days: number) => fixtureDevice(i, pro, { status: { sub_state: 'active', sub_end_date: daysFromNow(days) } })

/** Named fleets for one flow, e.g. `?test-fleet=transfer`; in the `transfer*` ones device 0 is the target */
const scenarios: Record<string, () => TestFleet> = {
  // One Pro on a live yearly plan
  transfer: () => toFleet([transferTarget(), yearlyPro(1, 200)]),
  // Several Pros: yearly, one-shot, monthly
  'transfer-many': () => toFleet([transferTarget(), yearlyPro(1, 200), oneShotPro(2, 540), monthlyPro(3, 18)]),
}

/** The fixture fleet replacing the account's, or null when fixtures are off. */
export const activeTestFleet = (): TestFleet | null => {
  const value = testFleetValue()
  if (!value) return null
  if (isFleetSize(value)) return buildTestFleet(Number(value))
  return scenarios[value]?.() ?? null
}

/**
 * `GET subscription_transfer` for a fixture device of a `transfer*` scenario (null otherwise, the
 * real API answers): the Pro Max can take over the subscription of any of the Pros.
 */
export const testTransferOptions = (deviceId: number): SubscriptionTransferOptions | null => {
  const value = testFleetValue()
  if (!value?.startsWith('transfer')) return null
  const { devices, subscriptions } = scenarios[value]()
  const [target, ...sources] = devices
  if (deviceId !== target.id) return { target: deviceId, eligible: false, reasons: ['target_covered'], sources: [] }
  return {
    target: target.id,
    eligible: true,
    reasons: [],
    sources: sources.map((source) => ({
      id: source.id,
      serial: source.serial,
      name: source.name,
      type: source.type,
      subscription_end: source.tracker_status.sub_end_date,
      recurring: subscriptions.some((sub) => sub.serial === source.serial && sub.current_subscription),
      transferable: true,
      reasons: [],
    })),
  }
}

/** `POST subscription_transfer` for a fixture device of a `transfer*` scenario (null otherwise) */
export const testTransferResult = (deviceId: number, sourceId: number): SubscriptionTransferResult | null => {
  const source = testTransferOptions(deviceId)?.sources.find((s) => s.id === sourceId)
  if (!source) return null
  return { source: source.serial, target: fixtureSerial(0), source_end: daysFromNow(TRANSFER_SOURCE_GRACE_DAYS), target_end: source.subscription_end, recurring: source.recurring }
}
